import { NotFoundError } from '@cian/peperrors';

import { trackDeleteMessageSuccess } from './tracking';
import { clearActiveReplyMessage } from '../../actions/chat/actions';
import { markMessageAsDeleted, restoreMessage } from '../../actions/sendMessage';
import { currentChatActiveReplyMessageIdSelector, currentChatOpponentIdSelector, userSelector } from '../../selectors';
import { performDeleteMessage } from '../../services/performDeleteMessage';
import { showAlert } from '../../slices/alertSlice';
import { closeDeleteMessageModal, saveDeletedMessageId, TDeletePlaceType } from '../../slices/deleteMessageSlice';
import { createAppAsyncThunk } from '../../store/createAppAsyncThunk';
import { EExceptionDomain } from '../../types/exceptionDomains';

export interface IDeleteMessageParameters {
  chatId: string;
  messageId: string;
  type: TDeletePlaceType;
}

const DELETE_MESSAGE_FAIL = 'deleteMessage.fail';
const DELETE_MESSAGE_SUCCESS = 'deleteMessage.success';

export const deleteMessage = createAppAsyncThunk(
  'messages/deleteMessage',
  async ({ messageId, chatId }: IDeleteMessageParameters, { getState, dispatch, extra: context }) => {
    const {
      logger,
      telemetry,
      custom: { messagesMap },
      config,
    } = context;

    const messageItem = messagesMap.get(messageId);

    if (!messageItem) {
      const exception = new NotFoundError({
        domain: EExceptionDomain.Messages,
        message: 'Сообщение для удаления не найдено',
        details: {
          chatId,
          messageId,
        },
      });

      logger.error(exception);

      return;
    }

    const { rawMessage } = messageItem.message;
    const state = getState();
    const user = userSelector(state);
    const shouldChangeUnreadCount = rawMessage.userId !== user?.userId;

    const deleteMessageText = config.get<string>('chats.deletedMessageText') || 'Сообщение удалено';

    dispatch(saveDeletedMessageId());
    dispatch(closeDeleteMessageModal());
    dispatch(
      markMessageAsDeleted({
        message: {
          ...rawMessage,
          status: 'deleted',
          text: deleteMessageText,
          reply: null,
        },
        messagesMap,
        shouldDecreaseUnread: shouldChangeUnreadCount,
      }),
    );

    const isDeleted = await performDeleteMessage(context, { parameters: { chatId, messageId } });

    if (!isDeleted) {
      dispatch(showAlert({ message: 'Не удалось удалить сообщение' }));
      dispatch(
        restoreMessage({
          message: rawMessage,
          messageId,
          messagesMap,
          shouldIncreaseUnread: shouldChangeUnreadCount,
        }),
      );
      telemetry.increment(DELETE_MESSAGE_FAIL);

      return;
    }

    const opponentId = currentChatOpponentIdSelector(getState());
    const withPhoto = Boolean(
      rawMessage.files && (rawMessage.files.images.length || rawMessage.files.documents?.length),
    );

    telemetry.increment(DELETE_MESSAGE_SUCCESS);
    trackDeleteMessageSuccess({ chatId, withPhoto, opponentId });

    const activeReplyMessageId = currentChatActiveReplyMessageIdSelector(state);
    if (activeReplyMessageId === messageId) {
      dispatch(clearActiveReplyMessage({ chatId }));
    }
  },
);
